import type { ScannedMachineDraft } from './scannerDb'

export const SCAN_EXPORT_FORMAT = 'myofade-machine-scan' as const
export const SCAN_EXPORT_VERSION = 1 as const

export interface ScannedMachineExportRow {
  id: string
  name: string
  qrPayload: string
  createdAt: number
  muscleImage: {
    mime: string
    /** Base64 (no data: prefix) for later AI muscle tagging */
    base64: string
  }
}

export interface MachineScanExport {
  format: typeof SCAN_EXPORT_FORMAT
  version: typeof SCAN_EXPORT_VERSION
  exportedAt: number
  machines: ScannedMachineExportRow[]
}

async function blobToBase64(blob: Blob): Promise<string> {
  if (typeof FileReader !== 'undefined') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : ''
        const comma = result.indexOf(',')
        resolve(comma >= 0 ? result.slice(comma + 1) : result)
      }
      reader.onerror = () => reject(reader.error ?? new Error('Could not read image'))
      reader.readAsDataURL(blob)
    })
  }
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

export async function buildScanExport(
  drafts: ScannedMachineDraft[],
  exportedAt = Date.now(),
): Promise<MachineScanExport> {
  const machines: ScannedMachineExportRow[] = []
  for (const d of drafts) {
    machines.push({
      id: d.id,
      name: d.name,
      qrPayload: d.qrPayload,
      createdAt: d.createdAt,
      muscleImage: {
        mime: d.muscleImageMime || d.muscleImage.type || 'application/octet-stream',
        base64: await blobToBase64(d.muscleImage),
      },
    })
  }
  return {
    format: SCAN_EXPORT_FORMAT,
    version: SCAN_EXPORT_VERSION,
    exportedAt,
    machines,
  }
}

export function serializeScanExport(exp: MachineScanExport): string {
  return `${JSON.stringify(exp, null, 2)}\n`
}

export function scanExportFilename(exportedAt = Date.now()): string {
  const stamp = new Date(exportedAt).toISOString().slice(0, 10)
  return `myofade-machine-scan-${stamp}.json`
}
